import { useContext, useEffect, useState } from "react";
import { NavigateFunction } from "react-router-dom";
import {
  resolveSignInError,
  SIGNIN_RESPONSE_ERROR_MAPPING,
} from "src/business/errorMapping";
import { UserContext } from "src/Utils/Context/UserContext";
import {
  OwnerCreatePayload,
  OwnerSignInPayload,
  OwnerUpdatePayload,
  OwnerUpdateResponse,
} from "src/business/account/owner";
import { ApiResponse } from "src/business/api";
import { sha256 } from "js-sha256";
import { useMutation, useQuery } from "@tanstack/react-query";
import { API_URL, getVersionedUrl } from "src/Utils/API";

type Props = {
  navigate: NavigateFunction;
};

// Login failures describe themselves in the body: an ApiResponse for most of
// them, but a bare string when the account is not of the requested userType.
// Read it as text first so the plain-string case doesn't blow up on JSON.parse.
const readErrorMessage = async (response: Response): Promise<string | null> => {
  const body = await response.text().catch(() => "");

  if (!body) {
    return null;
  }

  try {
    const parsed = JSON.parse(body) as Partial<ApiResponse> | string;
    return typeof parsed === "string"
      ? parsed
      : (parsed.message ?? parsed.error ?? null);
  } catch {
    return body;
  }
};

export const useSignIn = ({ navigate }: Props) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { refetch, user, isLoading: userLoading } = useContext(UserContext);

  const Authenticate = async ({ email, password }: OwnerSignInPayload) => {
    setError(null);

    if (!email || !password) {
      setError(SIGNIN_RESPONSE_ERROR_MAPPING["Missing credentials"]);
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${getVersionedUrl()}/users/login`, {
        method: "POST",
        body: JSON.stringify({
          username: email,
          password: sha256(password),
          userType: "OWNER",
        }),
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (response.status === 200) {
        refetch();
        // Keep the loading state until UserContext catches up and redirects.
        return;
      }

      setError(
        resolveSignInError(response.status, await readErrorMessage(response)),
      );
    } catch (e) {
      console.log(e);
      setError(SIGNIN_RESPONSE_ERROR_MAPPING["An error occurred"]);
    }

    setLoading(false);
  };

  useEffect(() => {
    if (!userLoading && user) {
      const rootPath = "/restaurant";
      navigate(rootPath, { replace: true });
    }
  }, [userLoading, user, navigate]);

  return { Authenticate, loading, error, setError };
};

export const useSignUp = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const signUp = (payload: OwnerCreatePayload, onSuccess: () => void) => {
    setLoading(true);

    fetch(`${getVersionedUrl()}/users/signup`, {
      method: "POST",
      body: JSON.stringify(payload),
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    })
      .then((response) => {
        const status = response.status;

        if (status === 200) {
          onSuccess();
        } else if (status === 401) {
          setError(SIGNIN_RESPONSE_ERROR_MAPPING["User not found"]);
        } else if (status === 403) {
          setError(SIGNIN_RESPONSE_ERROR_MAPPING["Email not verified"]);
        } else {
          setError(SIGNIN_RESPONSE_ERROR_MAPPING["An error occurred"]);
        }
      })
      .catch((e) => {
        console.log(e);
        setError(SIGNIN_RESPONSE_ERROR_MAPPING["An error occurred"]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return { signUp, loading, error, setError };
};

export const useSendPasswordResetEmail = (email?: string) =>
  useQuery({
    queryKey: ["sendPasswordResetEmail"],
    queryFn: async () => {
      const response = await fetch(
        `${getVersionedUrl()}/users/password-reset`,
        {
          method: "POST",
          body: JSON.stringify({ email, userType: "OWNER" }),
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        },
      );
      const bodyData = await response.json();
      if (!bodyData.success) {
        throw new Error("Failed to fetch bookings");
      }
    },
    enabled: false,
  });

// PUT /web/staff/me — updates the signed-in owner's editable profile fields.
// On success it refreshes UserContext so the app reflects the saved values.
export const useUpdateOwnerProfile = (
  onSuccess?: () => void,
  onError?: (error: Error) => void,
) => {
  const { refetch } = useContext(UserContext);

  return useMutation({
    mutationFn: async (payload: OwnerUpdatePayload) => {
      const response = await fetch(`${API_URL}/staff/me`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include",
      });
      const data: OwnerUpdateResponse = await response.json();
      if (!data.success) {
        throw new Error(data.error ?? "Failed to update profile");
      }
      return data;
    },
    onSuccess: () => {
      refetch();
      onSuccess?.();
    },
    onError,
  });
};

export const useVerifyEmail = () =>
  useMutation({
    mutationFn: async (token: string) => {
      const response = await fetch(`${API_URL}/verify-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const bodyData = await response.json();
      if (!bodyData.success) {
        throw new Error("Failed to verify email");
      }
      return bodyData as { success: boolean; isMobile: boolean };
    },
  });
